<template>
  <div class="flex items-center justify-center">
    <router-link
      :to="dashboardPath"
      replace
      class="flex items-center justify-center rounded-xl p-1 min-w-[2.75rem] min-h-[2.75rem] md:min-w-0 md:min-h-0 outline-none transition-opacity duration-200 ease-out hover:opacity-80 focus-visible:ring-2 focus-visible:ring-accent-mint/70 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-ink"
    >
      <!--
        The mark is never typeset — it is always the raster mark, and never stretched:
        `h-8 w-auto` holds its 1.06:1 ratio. The navy mark carries a light ground, the
        mint one a dark ground; `hidden` drops the wrong one from the a11y tree too, so
        the accessible name is never announced twice.
      -->
      <template v-if="showBrandMark">
        <img
          :src="brandMarkOnLight"
          :alt="name"
          class="block w-auto h-8 dark:hidden"
        />
        <img
          :src="brandMarkOnDark"
          :alt="name"
          class="hidden w-auto h-8 dark:block"
        />
      </template>
      <img v-else :src="source" :alt="name" class="w-auto h-8" />
    </router-link>
  </div>
</template>
<script>
import { frontendURL } from 'dashboard/helper/URLHelper';

// What an UNBRANDED instance points at — either the stock Chatwoot thumbnail, or the Unirsal
// mark that `config/installation_config.yml` now seeds. Both mean "the operator has not chosen
// a logo", so the proper light/dark brand pair is right in either case. Anything else is a
// deliberate per-tenant logo and must be rendered untouched.
//
// The second entry exists because these two files have to agree: when the YAML started seeding
// the Unirsal mark, a fresh install looked to this component like a tenant that had chosen one,
// and it fell to the single-image branch — the mint mark with no dark variant, on a white
// sidebar in light mode.
const UNBRANDED_THUMBNAILS = [
  '/brand-assets/logo_thumbnail.svg',
  '/brand-assets/unirsal-logo-light.png',
  // The stale CDN mark, and the reason it has to be listed here rather than fixed by a deploy.
  //
  // `LOGO_THUMBNAIL` is an `InstallationConfig` ROW, and `ConfigLoader` runs with
  // `reconcile_only_new: true` — it creates missing rows and leaves existing ones untouched. So
  // `installation_config.yml` is a SEED, not a setting: editing it and shipping changes nothing
  // on an installation that already booted once. Staging still carries this old blue mark,
  // pointed at a CDN, and every deploy has correctly left it alone.
  //
  // It is not a per-tenant logo — it is our own superseded default, which is exactly what this
  // list means. Naming it here makes the light/dark brand pair apply to it, so an installation
  // carrying the stale row renders correctly from the deploy alone.
  'https://cdn.prod.unirsal.com/branding/unirsal-logo-blue.svg',
  // The value `LOGO_THUMBNAIL` is being moved TO, and it has to be listed here BEFORE that move,
  // not after.
  //
  // The row currently points at the Unirsal corporate mark, so the widget, the help-centre
  // portal and the survey page show a different company's logo than the dashboard tab. Pointing
  // it at the product mark fixes those — and would break this component on the way past, because
  // an unlisted source reads as "this tenant chose its own logo": one flat image on both grounds
  // instead of the light/dark pair, and the mint mark washed out on a white sidebar.
  //
  // Listed first, shipped in the same release as the migration.
  '/favicon-512x512.png',
];
const BRAND_MARK_ON_LIGHT = '/brand-assets/unirsal-logo-dark.png';
const BRAND_MARK_ON_DARK = '/brand-assets/unirsal-logo-light.png';

export default {
  props: {
    source: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    brandMarkOnLight() {
      return BRAND_MARK_ON_LIGHT;
    },
    brandMarkOnDark() {
      return BRAND_MARK_ON_DARK;
    },
    showBrandMark() {
      return !this.source || UNBRANDED_THUMBNAILS.includes(this.source);
    },
    dashboardPath() {
      return frontendURL(`accounts/${this.accountId}/dashboard`);
    },
  },
};
</script>
